import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHqAccess } from './settings/hqAccess';
import {
  getHealthIndicators, uploadHealthData,
  HealthIndicator, HealthDataResponse, setTokenProvider
} from '../api/client';

export function BusinessHealthPage() {
  const { user, getToken } = useAuth();
  const [data, setData] = useState<HealthDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState('');

  // BUILD-039: shows the upload control to whoever holds the server-resolved permission (the server checks it again on upload)
  const isAdmin = useHqAccess().can('hq.health.upload');
  void user;

  useEffect(() => {
    setTokenProvider(getToken);
    loadIndicators();
  }, []);

  async function loadIndicators() {
    setLoading(true);
    try {
      const result = await getHealthIndicators();
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult('');

    try {
      const text = await file.text();
      const result = await uploadHealthData(text, 'phase-details');
      setUploadResult(`Parsed ${result.parsed.jobs} projects, ${result.parsed.phases} phases, computed ${result.parsed.indicators} indicators.`);
      setShowUpload(false);
      loadIndicators();
    } catch (err: any) {
      setUploadResult(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  // Group indicators by category
  const groupedIndicators = (data?.indicators || []).reduce<Record<string, HealthIndicator[]>>((acc, ind) => {
    const group = ind.role === 'owner' ? 'Company Overview' :
                  ind.role === 'pm' ? 'Project Management' :
                  ind.role === 'cad' ? 'CAD / Permit' :
                  ind.role === 'sales' ? 'Sales' : 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(ind);
    return acc;
  }, {});

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Business Health</h1>
            {data?.lastUpload && (
              <p className="text-xs text-gray-500 mt-1">
                Data as of: {new Date(data.lastUpload.uploadedAt).toLocaleDateString()} ({data.lastUpload.jobCount} projects, {data.lastUpload.phaseCount} phases)
              </p>
            )}
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="px-4 py-2 bg-[#4AABDE] text-white text-sm font-medium rounded-md hover:bg-[#3a9bce] transition-colors"
            >
              Upload Data
            </button>
          )}
        </div>

        {/* Upload Panel */}
        {showUpload && (
          <div className="bg-white rounded-lg border border-[#4AABDE] p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Upload ProEdge Export</h3>
            <p className="text-sm text-gray-500 mb-3">
              Export your Phase Details report from ProEdge as CSV and upload it here.
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={uploading}
              className="text-sm text-gray-600"
            />
            {uploading && <p className="text-sm text-[#4AABDE] mt-2">Processing...</p>}
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <div className={`rounded-md px-4 py-3 mb-4 text-sm ${uploadResult.includes('failed') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {uploadResult}
            <button onClick={() => setUploadResult('')} className="float-right font-bold">&times;</button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
        )}

        {/* AI Summary */}
        {data?.summary && (
          <div className="bg-gradient-to-r from-[#1a2332] to-[#2a3a4d] rounded-lg p-4 mb-6 text-white">
            <p className="text-sm leading-relaxed">{data.summary}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 text-gray-500">Loading health data...</div>
        )}

        {/* No Data State */}
        {!loading && (!data?.indicators || data.indicators.length === 0) && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="text-gray-400 text-4xl mb-4">&#9636;</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No health data yet</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              {isAdmin
                ? 'Upload a ProEdge Phase Details CSV export to see health indicators.'
                : 'Health indicators will appear once data is uploaded by an admin.'}
            </p>
          </div>
        )}

        {/* Indicators by Group */}
        {!loading && Object.entries(groupedIndicators).map(([group, indicators]) => (
          <div key={group} className="mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{group}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {indicators.map((ind) => (
                <IndicatorCard key={ind.key} indicator={ind} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IndicatorCard({ indicator }: { indicator: HealthIndicator }) {
  const isRisk = indicator.category === 'risk' && typeof indicator.value === 'number' && indicator.value > 0;

  return (
    <div className={`bg-white rounded-lg border p-4 ${isRisk ? 'border-red-200' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 font-medium">{indicator.label}</p>
          <p className={`text-2xl font-bold mt-1 ${isRisk ? 'text-red-600' : 'text-gray-900'}`}>
            {indicator.value}{indicator.unit || ''}
          </p>
        </div>
        {indicator.trend && (
          <span className={`text-lg ${
            indicator.trend === 'up' ? 'text-green-500' : indicator.trend === 'down' ? 'text-red-500' : 'text-gray-400'
          }`}>
            {indicator.trend === 'up' ? '&#9650;' : indicator.trend === 'down' ? '&#9660;' : '&#9644;'}
          </span>
        )}
      </div>
      {indicator.description && (
        <p className="text-xs text-gray-500 mt-2">{indicator.description}</p>
      )}
    </div>
  );
}
